import {
  type ClientReturn,
  createClient,
  type QueryParams,
  type SanityClient,
} from "@sanity/client";

import {
  SANITY_API_VERSION,
  SANITY_DATASET,
  SANITY_PROJECT_ID,
} from "~/constants/sanity";

let client: SanityClient | null = null;
let draftClient: SanityClient | null = null;

const getSanityReadToken = (): string | undefined => {
  if (typeof window !== "undefined") {
    return undefined;
  }

  return process.env.SANITY_API_READ_TOKEN?.trim() || undefined;
};

export const hasSanityReadToken = (): boolean => Boolean(getSanityReadToken());

// Published content is public: no token on purpose. A token would be sent on
// every SSR read, and once it expires or is revoked Sanity answers 401 even for
// the public dataset, which the article loaders render as "Page not found"
// (PH-1332). Queries that reach private documents add it per request through
// `fetchPublishedWithReadToken`; the draft client below always carries it.
const createPublishedSanityClientConfig = () => ({
  projectId: SANITY_PROJECT_ID,
  dataset: SANITY_DATASET,
  useCdn: true,
  apiVersion: SANITY_API_VERSION,
});

const createDraftSanityClientConfig = () => {
  const token = getSanityReadToken();

  return {
    projectId: SANITY_PROJECT_ID,
    dataset: SANITY_DATASET,
    useCdn: false,
    apiVersion: SANITY_API_VERSION,
    perspective: "drafts" as const,
    ...(token ? { token } : {}),
  };
};

export const getSanityClient = (): SanityClient => {
  if (!client) {
    client = createClient(createPublishedSanityClientConfig());
  }

  return client;
};

export const getSanityDraftClient = (): SanityClient => {
  if (!draftClient) {
    draftClient = createClient(createDraftSanityClientConfig());
  }

  return draftClient;
};

const isTokenRejected = (error: unknown): error is { statusCode: number } =>
  typeof error === "object" &&
  error !== null &&
  "statusCode" in error &&
  (error.statusCode === 401 || error.statusCode === 403);

/**
 * Published read for queries that reach documents whose `_id` contains a dot
 * (`articleType.news`, `faq.<hash>`): Sanity keeps those private even in a
 * public dataset, so only a request with the read token returns them. The
 * token goes on this request alone; a missing or rejected token falls back to
 * the public read, so an expired token hides those documents instead of
 * failing the page (PH-1332).
 */
export const fetchPublishedWithReadToken = async <const G extends string>(
  query: G,
  params: QueryParams = {}
): Promise<ClientReturn<G>> => {
  const publishedClient = getSanityClient();
  const token = getSanityReadToken();

  if (!token) {
    return publishedClient.fetch(query, params);
  }

  try {
    // The token can also read drafts; the explicit perspective keeps this
    // read to published content whatever the API version's default is.
    return await publishedClient.fetch(query, params, {
      token,
      perspective: "published",
    });
  } catch (error) {
    if (!isTokenRejected(error)) {
      throw error;
    }

    console.error(
      `Sanity rejected SANITY_API_READ_TOKEN (HTTP ${error.statusCode}); reading public content only`
    );
    return publishedClient.fetch(query, params);
  }
};
